import { Link } from "@tanstack/react-router";
import type { Id } from "@convex/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";
import { api } from "@convex/_generated/api";
import type {
  ActorKindValue,
  TaskDateBasisValue,
  ActivityFormFieldTypeValue,
  ActivityReviewModeValue,
  ActivityReviewRoleValue,
  DateAnchorValue,
  ProgrammeValue,
} from "@convex/schemaEnums";
import type { NAMED_AT_ASSIGNMENT } from "@convex/schemaEnums";
import { LuArrowLeft } from "react-icons/lu";
import { Box, Button, Card, Heading, Text } from "@chakra-ui/react";
import { PageHeader } from "../components/PageHeader";
import { tagColor } from "@/lib/tagColor";

export type ReviewRole = ActivityReviewRoleValue;
export type ReviewMode = ActivityReviewModeValue | null;
// A catalogue step's: the copy's kinds plus "named when assigned".
export type ActorKind = ActorKindValue | typeof NAMED_AT_ASSIGNMENT;
export type TaskDateBasis = TaskDateBasisValue;
/** The two bases, plus the explicit absence of a deadline. */
export type TaskDeadlineChoice = TaskDateBasis | "none";
export type FieldType = ActivityFormFieldTypeValue;

export type TagOption = {
  _id: Id<"activityTags">;
  key: string;
  name: string;
  description: string | null;
  color: string | null;
  isActive: boolean;
};

// Derived from the queries, like PersonOption below and for the same reason:
// a hand-written shape plus an `as` cast compiles happily while disagreeing
// with what the backend actually returns.
export type ActivitySummary =
  FunctionReturnType<typeof api.activities.listAllActivities>[number];

export type ActivityDetail =
  NonNullable<FunctionReturnType<typeof api.activities.getActivityDetail>>;

// Derived from the query rather than hand-written. A local shape plus an `as`
// cast silences the compiler, so listCeMMies changing from an array to
// { stats, persons } broke at runtime with a green build. Deriving it means the
// next shape change is a type error instead.
export type PersonOption =
  FunctionReturnType<typeof api.persons.listCeMMies>["persons"][number];

export type PersonActivityRow =
  FunctionReturnType<typeof api.personActivities.listAllForAdmin>[number];

export type EditableField = {
  key: string;
  label: string;
  description: string;
  type: FieldType;
  required: boolean;
  optionsText: string;
  min: string;
  max: string;
  step: string;
  // File fields only: allow more than one upload.
  multiple: boolean;
  // True once the field has been persisted; its key must then stay stable so
  // previously-stored responses keep resolving. New fields derive their key
  // from the label until saved.
  persisted: boolean;
};

export type EditableTask = {
  // Stable for the life of the draft, so prerequisites survive reordering --
  // which positions would not. Turned into orders on save.
  localKey: string;
  prerequisiteKeys: string[];
  title: string;
  description: string;
  required: boolean;
  // Who performs it. One choice rather than a list -- a task has exactly one
  // actor -- so the role and the person are held beside the kind and only the
  // one the kind selects is read.
  actorKind: ActorKind;
  actorRole: ReviewRole;
  actorPersonId: string | null;
  // The three reviewer kinds (model §9), kept apart in the draft because they
  // are three different controls; recombined into one list on save.
  reviewerSupervisor: boolean;
  reviewerCoSupervisor: boolean;
  reviewerNamedAtAssignment: boolean;
  reviewerRoles: ReviewRole[];
  reviewerPersonIds: string[];
  reviewMode: ReviewMode;
  fields: EditableField[];
  // What a reviewer records when signing. Empty means a comment is the
  // whole review, which is what most tasks want.
  reviewFields: EditableField[];
  // A step completed by a RECORD rather than by work (model §6). Off for
  // nearly every task, and an alternative to the actor above rather than a
  // setting on top of it -- which is why the completion select carries both.
  satisfiedByParticipation: boolean;
  participationRoles: string[];
  participationSeriesId: string | null;
  participationExternalKind: string | null;
  // The step's own deadline. "none" is a state of its own rather than
  // something inferred from an empty number: a reader had no way to see that
  // deleting the digits was how you got back to having no deadline.
  dueBasis: TaskDeadlineChoice;
  dueOffsetDays: string;
};

/**
 * The draft's editable fields back into a stored form schema.
 *
 * Shared by the two forms on a task -- what the actor answers and what the
 * reviewer answers -- so a field type behaves identically in both.
 */
export function buildFormSchema(fields: EditableField[]) {
  if (fields.length === 0) {
    return null;
  }
  const numeric = (field: EditableField, value: string) =>
    (field.type === "number" || field.type === "range") && value !== "" ? Number(value) : undefined;
  return {
    fields: fields.map((field) => ({
      key: field.key,
      label: field.label,
      type: field.type,
      required: field.required,
      description: field.description || undefined,
      options:
        field.type === "select" && field.optionsText.trim()
          ? field.optionsText.split("\n").map((o) => o.trim()).filter(Boolean)
          : undefined,
      min: numeric(field, field.min),
      max: numeric(field, field.max),
      multiple: field.type === "file" && field.multiple ? true : undefined,
      step: numeric(field, field.step),
    })),
  };
}

/** One stored field into the draft's editable shape. Both forms use it. */
function toEditableField(field: {
  key: string;
  label: string;
  description?: string | null;
  type: FieldType;
  required: boolean;
  options?: string[];
  min?: number;
  max?: number;
  step?: number;
  multiple?: boolean;
}): EditableField {
  return {
    key: field.key,
    label: field.label,
    description: field.description ?? "",
    type: field.type,
    required: field.required,
    optionsText: (field.options ?? []).join("\n"),
    min: field.min === undefined ? "" : String(field.min),
    max: field.max === undefined ? "" : String(field.max),
    step: field.step === undefined ? "" : String(field.step),
    multiple: field.multiple ?? false,
    persisted: true,
  };
}

/** The draft's two date controls, back into the rule the backend takes. */
export function buildTaskDueRule(task: EditableTask) {
  const offset = Number(task.dueOffsetDays);
  if (task.dueBasis === "none"
    || task.dueOffsetDays.trim() === ""
    || !Number.isFinite(offset)
    || offset < 0) {
    return null;
  }
  return { basis: task.dueBasis, offsetDays: Math.round(offset) };
}

/**
 * The part of a step that says who does it and who signs it off. The editor's
 * controls work on this rather than on a whole draft, so the activity page can
 * use them to change one assigned copy.
 */
export type TaskPeople = Pick<
  EditableTask,
  | "actorKind"
  | "actorRole"
  | "actorPersonId"
  | "satisfiedByParticipation"
  | "participationRoles"
  | "reviewerSupervisor"
  | "reviewerCoSupervisor"
  | "reviewerNamedAtAssignment"
  | "reviewerRoles"
  | "reviewerPersonIds"
  | "reviewMode"
>;

/** The draft's actor controls, back into the single spec the backend takes. */
export function buildActor(task: TaskPeople): ActorSpec {
  switch (task.actorKind) {
    case "supervisor":
      return { kind: "supervisor" };
    case "coSupervisor":
      return { kind: "coSupervisor" };
    case "namedAtAssignment":
      return { kind: "namedAtAssignment" };
    case "role":
      return { kind: "role", role: task.actorRole };
    case "person":
      // Falls back rather than throwing: an unfinished picker should leave the
      // task with the assignee, not refuse the save with a type error.
      return task.actorPersonId === null
        ? { kind: "assignee" }
        : { kind: "person", personId: task.actorPersonId as Id<"persons"> };
    case "assignee":
      return { kind: "assignee" };
  }
}

/** The draft's three reviewer controls, back into what the backend takes. */
export function buildReviewers(task: TaskPeople): ReviewerSpec[] {
  return [
    ...(task.reviewerSupervisor ? [{ kind: "supervisor" } as const] : []),
    ...(task.reviewerCoSupervisor ? [{ kind: "coSupervisor" } as const] : []),
    ...(task.reviewerNamedAtAssignment ? [{ kind: "namedAtAssignment" } as const] : []),
    ...task.reviewerRoles.map((role) => ({ kind: "role", role }) as const),
    ...task.reviewerPersonIds.map((personId) => ({
      kind: "person",
      personId: personId as Id<"persons">,
    }) as const),
  ];
}

export function countReviewers(task: TaskPeople): number {
  return (task.reviewerSupervisor ? 1 : 0) + (task.reviewerCoSupervisor ? 1 : 0)
    + (task.reviewerNamedAtAssignment ? 1 : 0)
    + task.reviewerRoles.length + task.reviewerPersonIds.length;
}

export type ActivityDraft = {
  activityId: string | null;
  key: string;
  title: string;
  description: string;
  required: boolean;
  // Null is "not part of a programme": the activity then appears in
  // "My activities" only, never on a programme page or in the matrix.
  programme: ProgrammeValue | null;
  isActive: boolean;
  tagIds: string[];
  // A date rule is an anchor plus an offset (model §8). An empty offset means
  // the rule is unset, which is not the same as zero: no stated date at all.
  // Advice, never a gate: being early is allowed.
  startAnchor: DateAnchorValue;
  startOffsetDays: string;
  startFixedDay: string;
  // The gate, for a submission window that genuinely opens on a date.
  opensAnchor: DateAnchorValue;
  opensOffsetDays: string;
  opensFixedDay: string;
  dueAnchor: DateAnchorValue;
  dueOffsetDays: string;
  dueFixedDay: string;
  // "Comes after these." Many-to-many; the date kind is gone, because
  // "not before N days" is what the start rule already says.
  dependsOnActivityIds: string[];
  tasks: EditableTask[];
};

export type ReviewerSpec =
  | { kind: "supervisor" }
  | { kind: "coSupervisor" }
  | { kind: "namedAtAssignment" }
  | { kind: "role"; role: ReviewRole }
  | { kind: "person"; personId: Id<"persons"> };

export type ActorSpec =
  | { kind: "assignee" }
  | { kind: "supervisor" }
  | { kind: "coSupervisor" }
  | { kind: "namedAtAssignment" }
  | { kind: "role"; role: ReviewRole }
  | { kind: "person"; personId: Id<"persons"> };

// System admin is deliberately absent: it is an infrastructure role, not
// somebody anybody should be waiting on for a signature. It stays a valid
// stored value, so an activity that already names it keeps working.
export const reviewRoleOptions: Array<{ value: ReviewRole; label: string }> = [
  { value: "hrAdmin", label: "HR admin" },
  { value: "grantsAdmin", label: "Grants admin" },
  { value: "activityAdmin", label: "Activity admin" },
];

export const fieldTypeOptions: Array<{ value: FieldType; label: string }> = [
  { value: "text", label: "Text" },
  { value: "textarea", label: "Textarea" },
  { value: "date", label: "Date" },
  { value: "select", label: "Select" },
  { value: "url", label: "URL" },
  { value: "boolean", label: "Boolean" },
  { value: "file", label: "File upload" },
  { value: "number", label: "Number" },
  { value: "email", label: "Email" },
  { value: "range", label: "Range" },
  { value: "time", label: "Time" },
];

/**
 * A "YYYY-MM-DD" day, for reading. Parsed as UTC noon rather than midnight so
 * that no timezone can shunt it onto the day before.
 */
// Re-exported, not redefined: the definition moved to src/lib/activityDates.ts
// so that the overdue test can sit beside it, and every page asks the same
// question the same way.
export { viennaToday } from "@/lib/activityDates";

export function formatDay(day: string | null) {
  if (day === null || day === "") {
    return "—";
  }

  return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" }).format(
    new Date(`${day}T12:00:00Z`),
  );
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

export function emptyField(type: FieldType = "text"): EditableField {
  return {
    key: "",
    label: "",
    description: "",
    type,
    required: false,
    optionsText: "",
    min: "",
    max: "",
    step: "",
    multiple: false,
    persisted: false,
  };
}

let localKeyCounter = 0;

export function nextLocalKey(): string {
  localKeyCounter += 1;
  return `t${localKeyCounter}-${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyTask(): EditableTask {
  return {
    localKey: nextLocalKey(),
    prerequisiteKeys: [],
    title: "",
    description: "",
    required: true,
    actorKind: "assignee",
    actorRole: "activityAdmin",
    actorPersonId: null,
    reviewerSupervisor: false,
    reviewerCoSupervisor: false,
    reviewerNamedAtAssignment: false,
    reviewerRoles: [],
    reviewerPersonIds: [],
    reviewMode: null,
    fields: [],
    reviewFields: [],
    satisfiedByParticipation: false,
    participationRoles: [],
    participationSeriesId: null,
    participationExternalKind: null,
    dueBasis: "none",
    dueOffsetDays: "",
  };
}

export const taskDateBasisOptions: Array<{ value: TaskDateBasis; label: string; hint: string }> = [
  { value: "beforeActivityDue", label: "before the activity is due", hint: "The document two weeks ahead of the meeting" },
  { value: "afterTaskOpens", label: "after this step opens", hint: "Respond within five days of it becoming your move" },
];

export const anchorOptions: Array<{ value: DateAnchorValue; label: string; hint: string }> = [
  { value: "enrollmentStart", label: "Programme start", hint: "Day 0 is the enrollment start; needs an enrollment" },
  { value: "affiliationStart", label: "Affiliation start", hint: "Day 0 is the start at CeMM; onboarding" },
  { value: "assignmentDay", label: "Day of assignment", hint: "Day 0 is whenever this gets assigned" },
  { value: "fixedDay", label: "A fixed date", hint: "The same day for everyone" },
  {
    value: "eventDate",
    label: "The occasion it is for",
    // Named for what supplies the day rather than "an event", because the
    // event is not chosen here: it is chosen when the activity is assigned,
    // and an activity using this rule cannot be assigned without one.
    hint: "Day 0 is the day of the occasion the copy is made for; chosen at assignment",
  },
  {
    value: "afterActivity",
    label: "After what this comes after",
    // Named for what it waits on rather than "after an activity", because the
    // activity it waits on is not chosen here: it is whatever is listed under
    // "Comes after". Saying so on the option is what stops somebody picking
    // this with nothing listed and wondering why no date appears.
    hint: "Day 0 is when everything under “Comes after” is complete; set that first",
  },
];

export function emptyActivityDraft(): ActivityDraft {
  return {
    activityId: null,
    key: "",
    title: "",
    description: "",
    required: true,
    programme: null,
    isActive: true,
    tagIds: [],
    startAnchor: "assignmentDay",
    startOffsetDays: "",
    startFixedDay: "",
    opensAnchor: "fixedDay",
    opensOffsetDays: "",
    opensFixedDay: "",
    dueAnchor: "assignmentDay",
    dueOffsetDays: "",
    dueFixedDay: "",
    dependsOnActivityIds: [],
    tasks: [emptyTask()],
  };
}

/**
 * The draft fields for one date rule, back into the shape the backend takes.
 * Null when nothing was stated: an unset date, not a date of zero.
 */
export function buildDateRule(
  anchor: DateAnchorValue,
  offsetDays: string,
  fixedDay: string,
): { anchor: DateAnchorValue; offsetDays: number; fixedDay: string | null } | null {
  if (anchor === "fixedDay") {
    return fixedDay === "" ? null : { anchor, offsetDays: 0, fixedDay };
  }
  return offsetDays === "" ? null : { anchor, offsetDays: Number(offsetDays), fixedDay: null };
}

export function tagStyle(color: string | null) {
  return { borderColor: tagColor(color), color: tagColor(color) };
}

export function buildActivityDraft(detail: ActivityDetail): ActivityDraft {
  return {
    activityId: String(detail._id),
    key: detail.key,
    title: detail.title,
    description: detail.description,
    required: detail.required,
    // Absent on entries saved before the field existed; the same as none.
    programme: detail.programme ?? null,
    isActive: detail.isActive,
    tagIds: detail.tagIds.map(String),
    startAnchor: detail.recommendedStartRule?.anchor ?? "assignmentDay",
    startOffsetDays: detail.recommendedStartRule === null ? "" : String(detail.recommendedStartRule.offsetDays),
    startFixedDay: detail.recommendedStartRule?.fixedDay ?? "",
    opensAnchor: detail.opensOnRule?.anchor ?? "fixedDay",
    opensOffsetDays: detail.opensOnRule === null ? "" : String(detail.opensOnRule.offsetDays),
    opensFixedDay: detail.opensOnRule?.fixedDay ?? "",
    dueAnchor: detail.dueRule?.anchor ?? "assignmentDay",
    dueOffsetDays: detail.dueRule === null ? "" : String(detail.dueRule.offsetDays),
    dueFixedDay: detail.dueRule?.fixedDay ?? "",
    dependsOnActivityIds: detail.dependsOnActivityIds.map(String),
    // Orders are the catalogue's stable handle; the draft swaps them for local
    // keys so that reordering in the editor cannot scramble the links.
    tasks: detail.tasks.map((task) => ({
      localKey: `order-${task.order}`,
      prerequisiteKeys: task.prerequisiteOrders.map((order) => `order-${order}`),
      title: task.title,
      description: task.description,
      required: task.required,
      actorKind: task.actor.kind,
      actorRole: task.actor.kind === "role" ? task.actor.role : "activityAdmin",
      actorPersonId: task.actor.kind === "person" ? String(task.actor.personId) : null,
      reviewerSupervisor: task.reviewers.some((spec) => spec.kind === "supervisor"),
      reviewerCoSupervisor: task.reviewers.some((spec) => spec.kind === "coSupervisor"),
      reviewerNamedAtAssignment: task.reviewers.some((spec) => spec.kind === "namedAtAssignment"),
      reviewerRoles: task.reviewers.flatMap((spec) => spec.kind === "role" ? [spec.role] : []),
      reviewerPersonIds: task.reviewers.flatMap((spec) => spec.kind === "person" ? [String(spec.personId)] : []),
      reviewMode: task.reviewMode,
      fields: (task.formSchema?.fields ?? []).map(toEditableField),
      reviewFields: (task.reviewFormSchema?.fields ?? []).map(toEditableField),
      satisfiedByParticipation: task.participationCriteria !== undefined,
      participationRoles: task.participationCriteria?.roles.map(String) ?? [],
      participationSeriesId: task.participationCriteria?.seriesId === null
        ? null
        : String(task.participationCriteria?.seriesId ?? "") || null,
      participationExternalKind: task.participationCriteria?.externalKind ?? null,
      dueBasis: task.dueRule?.basis ?? "none",
      dueOffsetDays: task.dueRule ? String(task.dueRule.offsetDays) : "",
    })),
  };
}

export function ActivityAdminLoadingState({ title, description, breadcrumbs }: { title: string; description: string; breadcrumbs: Array<{ label: string; to?: string }> }) {
  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader title={title} description={description} breadcrumbs={breadcrumbs} />
      <Box px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        <Card.Root borderWidth="1px" borderColor="border" shadow="sm">
          <Card.Body p={6}>
            <Text fontSize="sm" color="fg.muted">Loading access…</Text>
          </Card.Body>
        </Card.Root>
      </Box>
    </Box>
  );
}

export function ActivityAdminAccessDenied({ title, description, breadcrumbs }: { title: string; description: string; breadcrumbs: Array<{ label: string; to?: string }> }) {
  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title={title}
        description={description}
        breadcrumbs={breadcrumbs}
        actions={
          <Button asChild variant="outline">
            <Link to="/activities">
              <LuArrowLeft />
              Back to activities
            </Link>
          </Button>
        }
      />
      <Box px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        <Card.Root borderWidth="1px" borderColor="orange.300" shadow="sm">
          <Card.Body gap={3} p={8}>
            <Heading size="xl">Access restricted</Heading>
            <Text maxW="2xl" fontSize="sm" color="fg.muted">
              If you need it, ask a system admin to make you an activity admin.
            </Text>
          </Card.Body>
        </Card.Root>
      </Box>
    </Box>
  );
}
